import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import {
  faTrash as DeleteIcon,
  faPen as EditIcon,
  faEye,
  faEyeSlash,
  faBookOpen as ViewIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Chip, ListItemText, Stack, Tooltip } from '@mui/material';
import { v4 as uuid4 } from 'uuid';

import CheckPermissions from '@/components/CheckPermissions';
import { PERMISSIONS } from '@/constants/auth.constants';
import { axiosClient } from '@/services/BaseApi.service';
import type { GridColumns } from '@/types/common';
import getDateTime from '@/utils/getDateTime.utils';

import { AnnouncementDialogType } from './AnnouncementDialog/AnnouncementDialog.constants';
import type {
  Announcement,
  AnnouncementDTO,
  AttachmentFile
} from './Announcements.types';

const truncateOrgs = (orgs: string[]) => {
  if (orgs.length > 3) {
    return `${orgs.slice(0, 3).join(', ')}...+${orgs.length - 3} more`;
  }
  if (orgs.length > 1) {
    return orgs.join(', ');
  }
  return orgs;
};

export const mapAnnouncements = (
  announcements: AnnouncementDTO[]
): Announcement[] => {
  return announcements.map((announcement) => {
    return {
      ...announcement,
      id: `${announcement.id}`,
      org_list: announcement.org_list || '',
      send_email: !!announcement.send_email,
      scheduled_at: announcement.scheduled_at || null,
      attachments:
        announcement.attachments?.map((attachment) => ({
          ...new File([], attachment.file_name),
          key: `${attachment.key}`,
          file_name: attachment.file_name,
          id: attachment.id,
          safe: true
        })) || [],
      org_ids:
        announcement.announcement_jobs?.map((job) => job.organization_id) || []
    };
  });
};

/**
 *
 * @param announcementId
 * @param fileKey
 * @returns Download the attachment file
 */
export const handleAttachmentClick = (
  announcementId: string,
  fileKey: string
) => {
  const downloadAttachment = async () => {
    const response = await axiosClient.get(
      `/announcements/${announcementId}/attachments/${fileKey}`
    );
    return window.open(response.data.download_url, '_blank');
  };
  return downloadAttachment();
};

export const createColumns = (
  classes: { [key: string]: string },
  userIsInternal: boolean,
  setOpen: (open: boolean) => void,
  setSelectedAnnouncement: (selectedAnnouncement: Announcement) => void,
  setType: (type: AnnouncementDialogType) => void,
  type: string,
  handleDelete?: (id: string) => void,
  handleEdit?: (
    row: Announcement | ((prev: Announcement) => Announcement)
  ) => void,
  handlePublish?: (rowInfo: Announcement, revert?: boolean) => void,
  handleTestEmail?: (rowInfo: Announcement) => void
) => {
  if (classes) {
    // temporary dummy code to avoid lint error until attachments column is needed
  }
  const columns: GridColumns[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Button
          variant="text"
          disableRipple
          onClick={() => {
            setOpen(true);
            setType(AnnouncementDialogType.VIEW);
            setSelectedAnnouncement(params.row);
          }}
          sx={{ py: 0, ml: -1, textAlign: 'left', overflow: 'hidden' }}
        >
          <ListItemText primary={params.row.title} />
        </Button>
      )
    },
    {
      field: 'body',
      headerName: 'Message',
      align: 'left',
      flex: 2,
      minWidth: 400,
      renderCell: (params) => {
        const maxLength = !userIsInternal ? 500 : 150;
        let body =
          params.row.body.length <= maxLength
            ? params.row.body
            : `${params.row.body.slice(0, maxLength)}...`;

        // Remove markdown characters
        body = body.replace(/[*_~`#]/gm, '').replaceAll('&x20;', '');

        return (
          <ListItemText
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              setOpen(true);
              setType(AnnouncementDialogType.VIEW);
              setSelectedAnnouncement(params.row);
            }}
            primary={body}
          />
        );
      }
    },
    {
      field: 'attachments',
      headerName: 'Attachments',
      align: 'left',
      minWidth: userIsInternal ? 230 : 330,
      isColumnVisible: userIsInternal,
      renderCell: (params) => (
        <Stack style={{ marginTop: '4px', alignItems: 'flex-start' }}>
          {params.row.attachments.map((file: AttachmentFile) => (
            <Chip
              key={uuid4()}
              label={
                file.file_name.length > 20
                  ? `${file.file_name.slice(0, 25)}...`
                  : file.file_name
              }
              size="small"
              className={classes.fileUploaderChip}
              classes={{ deleteIcon: classes.fileUploaderDeleteIcon }}
              onClick={() => handleAttachmentClick(params.row.id, file.key)}
            />
          ))}
        </Stack>
      )
    },
    {
      field: 'orgList',
      headerName: 'Organizations',
      renderCell: (params) => (
        <Stack
          direction="row"
          style={{
            maxWidth: 300,
            marginTop: 4,
            marginBottom: 4,
            flexWrap: 'wrap'
          }}
        >
          {params.row.org_list !== ''
            ? truncateOrgs(JSON.parse(params.row.org_list))
            : ''}
        </Stack>
      ),
      align: 'left',
      isColumnVisible: userIsInternal,
      flex: 4,
      maxWidth: 300
    },
    {
      field: 'send_email',
      headerName: 'Email',
      isColumnVisible: userIsInternal && type === 'draft',
      type: 'boolean'
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      renderCell: (params) => (
        <ListItemText primary={getDateTime(params.row.created_at)} />
      ),
      isColumnVisible: userIsInternal && type === 'draft',
      flex: 5,
      maxWidth: 200
    },
    {
      field: 'scheduled_at',
      headerName: 'Scheduled At',
      renderCell: (params) => (
        <Stack>
          <ListItemText
            primary={
              params.row.scheduled_at
                ? getDateTime(params.row.scheduled_at)
                : ''
            }
          />
          {params.row.published_at === null &&
            params.row.published_by_id !== null && (
              <Chip
                sx={{ width: 'min-content' }}
                label="Publishing Pending"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
        </Stack>
      ),
      isColumnVisible: userIsInternal,
      flex: 5,
      maxWidth: 200
    },
    {
      field: 'published_at',
      headerName: 'Published At',
      renderCell: (params) => (
        <ListItemText
          sx={{ overflow: 'hidden' }}
          primary={getDateTime(params.row.published_at)}
        />
      ),
      isColumnVisible: type === 'published',
      flex: 5,
      maxWidth: 200
    },
    {
      headerName: 'View',
      field: 'view',
      renderCell: (params) => (
        <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap' }} spacing={0.5}>
          <Button
            color="primary"
            onClick={() => {
              setOpen(true);
              setType(AnnouncementDialogType.VIEW);
              setSelectedAnnouncement(params.row);
            }}
          >
            <FontAwesomeIcon icon={ViewIcon} />
          </Button>
        </Stack>
      ),
      isColumnVisible: type === 'published'
    },
    {
      headerName: 'Actions',
      field: 'actions',
      renderCell: (params) => (
        <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap' }} spacing={0.5}>
          <CheckPermissions permissions={PERMISSIONS.ANNOUNCEMENT_UPDATE}>
            <Button
              onClick={() => handleEdit?.(params.row)}
              disabled={!!params.row.published_by_id}
            >
              <FontAwesomeIcon icon={EditIcon} />
            </Button>
          </CheckPermissions>

          <CheckPermissions permissions={PERMISSIONS.ANNOUNCEMENT_DELETE}>
            <Button onClick={() => handleDelete?.(params.row.id)}>
              <FontAwesomeIcon icon={DeleteIcon} />
            </Button>
          </CheckPermissions>

          <CheckPermissions permissions={PERMISSIONS.ANNOUNCEMENT_PUBLISH}>
            {!params.row.published_by_id && (
              <Tooltip placement="top" title="Publish">
                <Button
                  disabled={params.row.org_list === ''}
                  onClick={() => handlePublish?.(params.row)}
                >
                  <FontAwesomeIcon icon={faEye} />
                </Button>
              </Tooltip>
            )}
            {params.row.published_by_id && (
              <Tooltip placement="top" title="Unpublish">
                <Button
                  disabled={params.row.org_list === ''}
                  onClick={() => handlePublish?.(params.row, true)}
                >
                  <FontAwesomeIcon icon={faEyeSlash} />
                </Button>
              </Tooltip>
            )}
            <Tooltip placement="top" title="Send Test Email">
              <Button onClick={() => handleTestEmail?.(params.row)}>
                <FontAwesomeIcon icon={faEnvelope} />
              </Button>
            </Tooltip>
          </CheckPermissions>
        </Stack>
      ),
      minWidth: 200,
      isColumnVisible: type === 'draft'
    }
  ];

  return columns;
};

export default truncateOrgs;
